/**
 * 🧲 MAGNETIC BUTTON - Bouton d'accroche dynamique
 * 
 * "Une grande promesse est l'âme de toute publicité" - Samuel Johnson
 * 
 * Affiche un message magnétique selon l'état de l'utilisateur.
 * Change dynamiquement pour créer de l'urgence et de la curiosité !
 */

import React from 'react';
import { motion } from 'framer-motion';
import { ChevronRight, Flame, Heart, Sparkles, Bell } from 'lucide-react';
import { Button } from '../ui/button';

export const MagneticButton = ({ 
  message,
  onClick,
  isHighUrgency = false,
  className = '',
  isDarkMode = false
}) => {
  // Message par défaut
  const displayText = message?.text || '💜 Découvrir';
  const subText = message?.subtext;

  // Animation selon l'urgence
  const pulseAnimation = isHighUrgency ? {
    scale: [1, 1.02, 1],
    boxShadow: [
      '0 0 30px rgba(236, 72, 153, 0.5)',
      '0 0 50px rgba(236, 72, 153, 0.7)',
      '0 0 30px rgba(236, 72, 153, 0.5)'
    ]
  } : {};

  return (
    <motion.div
      animate={pulseAnimation}
      transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
      className="relative"
    >
      {/* Badge de notification si haute urgence */}
      {isHighUrgency && (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="absolute -top-2 -right-2 z-10"
        >
          <div className="relative">
            <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
              <Bell className="w-3 h-3 text-white" />
            </div>
            <motion.div
              className="absolute inset-0 bg-red-500 rounded-full"
              animate={{ scale: [1, 1.5], opacity: [0.5, 0] }}
              transition={{ duration: 1, repeat: Infinity }}
            />
          </div>
        </motion.div>
      )}

      <Button
        onClick={onClick}
        className={`px-8 py-4 rounded-full font-bold text-lg flex flex-col items-center gap-1 shadow-2xl hover:scale-105 transition-all text-white ${className}`}
        style={{
          background: isHighUrgency 
            ? 'linear-gradient(135deg, #F97316 0%, #EC4899 50%, #8B5CF6 100%)'
            : 'linear-gradient(135deg, #EC4899 0%, #8B5CF6 50%, #EC4899 100%)',
          backgroundSize: '200% 200%',
          animation: 'gradient-shift 3s ease infinite',
          boxShadow: isHighUrgency 
            ? '0 0 40px rgba(249, 115, 22, 0.6)'
            : '0 0 30px rgba(236, 72, 153, 0.5)',
          minWidth: '200px'
        }}
      >
        <span className="flex items-center gap-2">
          {isHighUrgency ? (
            <motion.span
              animate={{ rotate: [0, 15, -15, 0] }}
              transition={{ duration: 0.5, repeat: Infinity }}
            >
              🔥
            </motion.span>
          ) : (
            <motion.span
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 0.8, repeat: Infinity }}
            >
              💜
            </motion.span>
          )}
          {displayText}
          <ChevronRight className="w-5 h-5" />
        </span>
        
        {subText && (
          <span className="text-xs font-normal opacity-90">
            {subText}
          </span>
        )}
      </Button>
    </motion.div>
  );
};

export default MagneticButton;
